// ROI Reporting — links measured biometric "then vs now" change to a
// defensible, cited £ value per metric (sleep duration, sleep quality,
// resting heart rate, HRV, vigorous activity), plus a derived workplace-
// injuries saving and a £0 staff-retention placeholder pending evidence.
//
// Central-admin gated (see router.tsx / AdminSideNav centralOnly). Pure
// client-side calculator in ../data/model.ts — no backend calls. Built with
// shadcn/ui (scoped Tailwind, see src/styles/roi-shadcn.css) rather than MUI —
// flat, bordered, no glow or motion.

import React, { useMemo, useState } from 'react';
import { useTheme } from '@mui/material';
import { BookOpen, Presentation, SlidersHorizontal, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  DEFAULT_BUSINESS_METRIC_INPUTS,
  DEFAULT_COMPANY_INPUTS,
  DEFAULT_LEVER_INPUTS,
  DEMO_COMPANY_INPUTS,
  DEMO_LEVER_INPUTS,
  computeRoi,
} from '../data/model';
import type {
  BusinessMetricId,
  BusinessMetricInputs,
  RoiCompanyInputs,
  RoiLeverId,
  RoiLeverInputs,
} from '../data/model';
import { AssumptionsSection } from '../sections/AssumptionsSection';
import { SavingsSection } from '../sections/SavingsSection';
import { MethodologySection } from '../sections/MethodologySection';
import '../../../../../styles/roi-shadcn.css';

type SectionKey = 'assumptions' | 'savings' | 'methodology';

const NAV: { key: SectionKey; label: string; icon: React.ReactElement }[] = [
  { key: 'assumptions', label: 'Company Numbers', icon: <SlidersHorizontal className='size-4' /> },
  { key: 'savings', label: 'Projected ROI', icon: <Wallet className='size-4' /> },
  { key: 'methodology', label: 'Methodology & Sources', icon: <BookOpen className='size-4' /> },
];

// Navigating away from this route (e.g. to Programme Profile) unmounts it, so
// plain useState loses edits on return. Company numbers persist in
// localStorage so they survive that round trip; lever inputs stay session-
// only since the ticket only asked for company numbers to stick.
const COMPANY_STORAGE_KEY = 'peak4.roiReporting.companyInputs';
// Business metrics are real client-reported figures, not demo session state,
// so they persist the same way company inputs do.
const BUSINESS_METRICS_STORAGE_KEY = 'peak4.roiReporting.businessMetricInputs';

const loadStoredCompanyInputs = (): RoiCompanyInputs => {
  try {
    const raw = localStorage.getItem(COMPANY_STORAGE_KEY);
    if (!raw) return DEFAULT_COMPANY_INPUTS;
    return { ...DEFAULT_COMPANY_INPUTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_COMPANY_INPUTS;
  }
};

const loadStoredBusinessMetricInputs = (): BusinessMetricInputs => {
  try {
    const raw = localStorage.getItem(BUSINESS_METRICS_STORAGE_KEY);
    if (!raw) return DEFAULT_BUSINESS_METRIC_INPUTS;
    return { ...DEFAULT_BUSINESS_METRIC_INPUTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULT_BUSINESS_METRIC_INPUTS;
  }
};

export const RoiReporting: React.FC = () => {
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';

  const [section, setSection] = useState<SectionKey>('assumptions');
  const [company, setCompany] = useState<RoiCompanyInputs>(loadStoredCompanyInputs);
  const [leverInputs, setLeverInputs] = useState<RoiLeverInputs>(DEFAULT_LEVER_INPUTS);
  const [businessMetricInputs, setBusinessMetricInputs] =
    useState<BusinessMetricInputs>(loadStoredBusinessMetricInputs);
  // Sales/demo toggle (PEAK-271): swaps in a fixed preset for a clean, consistent
  // story in sales meetings, instead of requiring the buyer to enter real numbers.
  // Session-only — doesn't persist, so leaving the page always lands back on the
  // live-client editable view.
  const [salesMode, setSalesMode] = useState(false);

  const effectiveCompany = salesMode ? DEMO_COMPANY_INPUTS : company;
  const effectiveLeverInputs = salesMode ? DEMO_LEVER_INPUTS : leverInputs;
  const result = useMemo(
    () => computeRoi(effectiveCompany, effectiveLeverInputs, businessMetricInputs),
    [effectiveCompany, effectiveLeverInputs, businessMetricInputs],
  );

  const handleCompanyChange = (next: RoiCompanyInputs) => {
    setCompany(next);
    localStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify(next));
  };

  const handleLeverInputChange = (id: RoiLeverId, field: 'then' | 'now', value: number) => {
    setLeverInputs((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleBusinessMetricInputChange = (id: BusinessMetricId, field: 'then' | 'now', value: number) => {
    setBusinessMetricInputs((prev) => {
      const next = { ...prev, [id]: { ...prev[id], [field]: value } };
      localStorage.setItem(BUSINESS_METRICS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const handleReset = () => {
    setCompany(DEFAULT_COMPANY_INPUTS);
    localStorage.removeItem(COMPANY_STORAGE_KEY);
    setLeverInputs(DEFAULT_LEVER_INPUTS);
    setBusinessMetricInputs(DEFAULT_BUSINESS_METRIC_INPUTS);
    localStorage.removeItem(BUSINESS_METRICS_STORAGE_KEY);
  };

  return (
    <div className={cn('roi-page', isDark && 'dark')}>
      <div className='flex flex-col gap-5 p-6'>
        {/* Title and section switcher share a row so the sections themselves get
            the page's full width — a 220px side rail spent on three links left
            the content cramped on wide screens. */}
        <div className='flex flex-wrap items-end justify-between gap-4'>
          <div>
            <h1 className='text-lg font-semibold'>ROI Reporting</h1>
            <p className='text-sm text-muted-foreground'>
              {salesMode
                ? 'Sales demo: fixed assumptions and a flat 10% improvement per metric, for a clear figure in sales meetings.'
                : 'Metric-linked ROI model: each figure traces back to one published study plus a stated assumption.'}
            </p>
          </div>

          <Button
            variant={salesMode ? 'default' : 'outline'}
            size='sm'
            onClick={() => setSalesMode((v) => !v)}
            aria-pressed={salesMode}
          >
            <Presentation />
            {salesMode ? 'Sales Demo: ON' : 'Sales Demo'}
          </Button>

          <nav aria-label='ROI Reporting sections'>
            <ul className='flex flex-wrap gap-1 rounded-lg border border-border bg-card p-1.5'>
              {NAV.map((item) => {
                const active = item.key === section;
                return (
                  <li key={item.key}>
                    <button
                      type='button'
                      onClick={() => setSection(item.key)}
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        active
                          ? 'bg-secondary text-secondary-foreground'
                          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        <div className='min-w-0'>
          {section === 'assumptions' && (
            <AssumptionsSection
              company={effectiveCompany}
              onChange={handleCompanyChange}
              onReset={handleReset}
              businessMetricInputs={businessMetricInputs}
              onBusinessMetricInputChange={handleBusinessMetricInputChange}
              disabled={salesMode}
            />
          )}
          {section === 'savings' && (
            <SavingsSection
              leverInputs={effectiveLeverInputs}
              onLeverInputChange={handleLeverInputChange}
              businessMetricInputs={businessMetricInputs}
              result={result}
              onEditCompanyNumbers={() => setSection('assumptions')}
              disabled={salesMode}
            />
          )}
          {section === 'methodology' && <MethodologySection />}
        </div>
      </div>
    </div>
  );
};
